import {
  bindWorkbook,
  checkWorkbook,
  mayRecalculateFor,
  nameWorkbookCompany,
  officeReady,
  onWorkbookCompanyEvent,
  persistWorkbookCompany,
  tiedByCell,
  workbookCompany,
} from '../services/workbookCompany';
import type { RecalculationCause, WorkbookCompany } from '../services/workbookCompany';
import {
  moveFailed,
  moveStarted,
  owedRecalculation,
  rearmRecalculationAfterBinding,
  resumeUnfinishedRecalculation,
  retryRecalculationAfterBinding,
  retryUnfinishedMove,
  unfinishedMove,
} from '../services/workbookRecalc';
import { lookForSignIn } from '../services/signInRecovery';

/** Sheet activation fires on every tab click; the panel re-reads at most this often for it. */
const ACTIVATION_REFRESH_MS = 2000;

/** Element.remove() is missing from the desktop ES5 fallback. */
function detach(element: Element | null | undefined): void {
  element?.parentNode?.removeChild(element);
}

export interface PaneCompany {
  companyId: string;
  companyName: string;
}

export interface SwitchTarget {
  id: string;
  name: string;
}

/**
 * What the line adds under the workbook's company, most pressing first. `moveUnfinished`: the workbook
 * was moved to it in the pane and its cells were not all recalculated since, this session or one before.
 * `bindingRecalculationFailed`: a cell tied the workbook and recalculating after that failed. `tiedNow`:
 * a cell tied the workbook this session, to the company the add-in was on, and it may belong to another
 * the user has.
 */
export type MatchNote = 'moveUnfinished' | 'bindingRecalculationFailed' | 'tiedNow';

export type WorkbookCompanyState =
  | { kind: 'none' }
  | { kind: 'match'; bound: WorkbookCompany; note: MatchNote | null }
  | { kind: 'mismatch'; bound: WorkbookCompany; current: PaneCompany; switchTarget: SwitchTarget | null };

export interface MatchFacts {
  moveUnfinished?: boolean;
  bindingRecalculationFailed?: boolean;
  tiedNow?: boolean;
}

/** What the pane shows for this workbook's company: nothing while signed out or unbound. */
export function workbookCompanyState(
  bound: WorkbookCompany | null,
  current: PaneCompany | null,
  reachable: (companyId: string) => SwitchTarget | null,
  facts: MatchFacts = {},
): WorkbookCompanyState {
  if (!bound || !current) return { kind: 'none' };
  if (bound.companyId !== current.companyId) {
    return { kind: 'mismatch', bound, current, switchTarget: reachable(bound.companyId) };
  }
  const note: MatchNote | null = facts.moveUnfinished ? 'moveUnfinished'
    : facts.bindingRecalculationFailed ? 'bindingRecalculationFailed'
      : facts.tiedNow ? 'tiedNow' : null;
  return { kind: 'match', bound, note };
}

const named = (name: string, fallback: string): string => name || fallback;

/** Why a pane action did nothing in a workbook that belongs to another company; `before` names the action. */
export function mismatchMessage(state: Extract<WorkbookCompanyState, { kind: 'mismatch' }>, before: string): string {
  return `This workbook uses ${named(state.bound.companyName, 'another company')}. `
    + `Switch back to it, or use ${named(state.current.companyName, 'this company')} for this workbook, before ${before}.`;
}

/** A move whose recalculation failed: the binding moved, the cells still show the old company's figures. */
export function finishMoveMessage(companyName: string): string {
  return `This workbook now uses ${named(companyName, 'another company')}, but recalculating it failed. `
    + 'Recalculate the workbook to finish moving it.';
}

/** Recalculating the cells after a cell tied the workbook failed: some may still hold another company's figures. */
export function bindingRecalculationFailedMessage(companyName: string): string {
  const name = named(companyName, 'this company');
  return `Could not recalculate after tying this workbook to ${name}. Recalculate it so every figure is ${name}'s.`;
}

/** What the line says the cells still owe the workbook's company, for a cause from owedRecalculation(). */
export function owedRecalculationMessage(cause: RecalculationCause, companyName: string): string {
  return cause === 'move' ? finishMoveMessage(companyName) : bindingRecalculationFailedMessage(companyName);
}

/**
 * Why converting to values would freeze figures the workbook's company is still owed, or null: its
 * cells were not all recalculated after a move or a binding, and the conversion does not fetch first.
 */
export function owedConversionRefusal(fetchesFirst: boolean): string | null {
  if (fetchesFirst) return null;
  const owed = owedRecalculation();
  return owed ? owedRecalculationMessage(owed, workbookCompany()?.companyName ?? '') : null;
}

/** "Use … for this workbook" clicked after the add-in moved to another company than the button named. */
export function choiceChangedMessage(companyName: string): string {
  return `The add-in is now on ${named(companyName, 'another company')}, so this workbook was not moved. Choose again.`;
}

/** A pane action in a workbook whose settings cannot be read, so which company it belongs to is unknown. */
export const UNREADABLE_WORKBOOK_MESSAGE =
  'Could not read which company this workbook belongs to, so nothing was done. Try again in a moment.';

/** Recalculate pressed with no session stored at all. */
export const SIGN_IN_FIRST_MESSAGE = 'Sign in to Bifrost first. Nothing was recalculated.';

/** A pane action while the company the add-in is signed in to is unknown. */
export const UNKNOWN_COMPANY_MESSAGE =
  'Could not tell which company the add-in is signed in to, so nothing was done. Sign in or refresh the pane, then try again.';

export interface WorkbookCompanyPanelHost {
  /** The company the add-in is signed in to; null when signed out or showing the sign-in screen. */
  currentCompany(): Promise<PaneCompany | null>;
  /** The company as the switcher lists it, when the user can switch to it. */
  reachable(companyId: string): SwitchTarget | null;
  /**
   * Whether a workbook tied to this company may belong to another the user has: it is a client's or a
   * demo company, or the user has more than one company of their own.
   */
  mayBelongElsewhere(companyId: string): boolean;
  switchTo(target: SwitchTarget): Promise<void>;
  /** Recalculates this workbook and no other. */
  recalculate(): Promise<void>;
  /** Why the panel's buttons may not act now, as while a journal action runs; null when they may. */
  blocked(): string | null;
  /** Says something went wrong, wherever the pane is showing. */
  report(message: string): void;
  /** A passing notice. */
  notice(message: string): void;
}

function element(tag: string, className: string, text: string): HTMLElement {
  const node = document.createElement(tag);
  node.className = className;
  node.textContent = text;
  return node;
}

/**
 * The pane's line saying which company this workbook belongs to, and the choice it offers when the
 * add-in is signed in to another one: switch back, or move the workbook to the current company.
 */
export class WorkbookCompanyPanel {
  private readonly mountId = 'workbook-company';
  private busy = false;
  /** What is drawn, so a refresh on every focus does not redraw (and re-announce) the same thing. */
  private drawn = '';
  /** Only the latest refresh draws: an earlier one can finish after it with an older reading. */
  private sequence = 0;
  private lastActivationRefresh = 0;

  constructor(private readonly host: WorkbookCompanyPanelHost) {}

  /**
   * Re-reads whenever this workbook's company may have changed under it: any workbook's pane can
   * switch the add-in's company, and a cell can bind the workbook. Called once.
   */
  watch(): void {
    window.addEventListener('focus', () => void this.refresh());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void this.refresh();
    });
    // A binding, or a recalculation after one that failed and now shows on the line.
    onWorkbookCompanyEvent(() => void this.refresh());
    Excel.run(async (context) => {
      context.workbook.worksheets.onActivated.add(async () => {
        // A recalculation after a binding or a move that Excel refused while a cell was being edited
        // can run now, and cells left on #SIGN_IN can be asked again once a session has appeared.
        retryRecalculationAfterBinding();
        void this.finishMove();
        void lookForSignIn();
        this.refreshOnActivation();
      });
      await context.sync();
    }).catch((error: unknown) => console.warn('Could not follow sheet activation for the workbook company:', error));
  }

  /** Tries again to recalculate a workbook moved in this pane whose recalculation failed or was held back. */
  private async finishMove(): Promise<void> {
    if (!unfinishedMove() || this.host.blocked()) return;
    const current = await this.host.currentCompany().catch(() => null);
    if (await retryUnfinishedMove(current?.companyId ?? null, () => this.host.recalculate())) void this.refresh();
  }

  refreshOnActivation(): void {
    const now = Date.now();
    if (now - this.lastActivationRefresh < ACTIVATION_REFRESH_MS) return;
    this.lastActivationRefresh = now;
    void this.refresh();
  }

  async refresh(): Promise<WorkbookCompanyState> {
    return (await this.readAndDraw()).state;
  }

  /**
   * Why a pane action may not run in this workbook, or null when it may; `before` names the action.
   * It may run only where the workbook belongs to the signed-in company, or to none yet: not in
   * another company's workbook, and not while either company is unknown.
   */
  async refusal(before: string): Promise<string | null> {
    const { state, current } = await this.readAndDraw();
    if (state.kind === 'mismatch') return mismatchMessage(state, before);
    if (!current) return UNKNOWN_COMPANY_MESSAGE;
    const verdict = checkWorkbook(current.companyId);
    if (verdict.kind === 'refuse') {
      return mismatchMessage({ kind: 'mismatch', bound: verdict.bound, current, switchTarget: null }, before);
    }
    return verdict.kind === 'proceed' ? null : UNREADABLE_WORKBOOK_MESSAGE;
  }

  private async readAndDraw(): Promise<{ state: WorkbookCompanyState; current: PaneCompany | null }> {
    const mine = ++this.sequence;
    await officeReady();
    const current = await this.host.currentCompany().catch(() => null);
    persistWorkbookCompany();
    let bound = workbookCompany();
    // Bound while the add-in did not know the company's name; the switcher's list does.
    if (bound && !bound.companyName) {
      bound = nameWorkbookCompany(this.host.reachable(bound.companyId)?.name ?? '') ?? bound;
    }
    // A recalculation an earlier session left owed, or one held while the add-in was on another
    // company, runs now, where it computes for the workbook's company.
    if (mayRecalculateFor(current?.companyId)) {
      resumeUnfinishedRecalculation();
      rearmRecalculationAfterBinding();
    }
    const owed = bound !== null ? owedRecalculation() : null;
    const state = workbookCompanyState(bound, current, id => this.host.reachable(id), {
      moveUnfinished: owed === 'move',
      bindingRecalculationFailed: owed === 'binding',
      tiedNow: bound !== null && tiedByCell() && this.host.mayBelongElsewhere(bound.companyId),
    });
    if (mine === this.sequence) this.draw(state);
    return { state, current };
  }

  clear(): void {
    this.drawn = '';
    detach(document.getElementById(this.mountId));
  }

  private async act(action: () => Promise<void>): Promise<void> {
    if (this.busy) return;
    const blocked = this.host.blocked();
    if (blocked) {
      this.host.notice(blocked);
      return;
    }
    this.busy = true;
    try {
      await action();
    } finally {
      this.busy = false;
      await this.refresh();
    }
  }

  private async useCurrent(offered: PaneCompany): Promise<void> {
    // Another workbook's pane can have moved the add-in since the button was drawn.
    const current = await this.host.currentCompany().catch(() => null);
    if (!current) return;
    if (current.companyId !== offered.companyId) {
      this.host.notice(choiceChangedMessage(current.companyName));
      return;
    }
    const blocked = this.host.blocked();
    if (blocked) {
      this.host.notice(blocked);
      return;
    }
    const company = { companyId: current.companyId, companyName: current.companyName };
    bindWorkbook(company);
    moveStarted(company);
    try {
      await this.host.recalculate();
    } catch (error) {
      // Said now, and on the line until the cells are recalculated.
      console.warn('Could not recalculate the workbook after moving it:', error);
      moveFailed();
      this.host.report(finishMoveMessage(current.companyName));
    }
  }

  private draw(state: WorkbookCompanyState): void {
    if (state.kind === 'none') {
      this.clear();
      return;
    }
    const key = JSON.stringify(state);
    if (key === this.drawn && document.getElementById(this.mountId)) return;
    this.drawn = key;

    const mount = this.mount();
    mount.textContent = '';
    if (state.kind === 'match') {
      const name = named(state.bound.companyName, 'this company');
      mount.className = 'workbook-company';
      mount.removeAttribute('role');
      mount.appendChild(element('span', 'wc-label', 'This workbook'));
      mount.appendChild(element('span', 'wc-name', name));
      const note = state.note === 'moveUnfinished' ? finishMoveMessage(state.bound.companyName)
        : state.note === 'bindingRecalculationFailed' ? bindingRecalculationFailedMessage(state.bound.companyName)
          : state.note === 'tiedNow' ? `Tied to ${name} just now. If it belongs to another company, switch to that company above.`
            : null;
      if (note) mount.appendChild(element('div', 'wc-note', note));
      return;
    }

    const boundName = named(state.bound.companyName, 'another company');
    const currentName = named(state.current.companyName, 'this company');
    mount.className = 'workbook-company workbook-company--mismatch';
    mount.setAttribute('role', 'alert');
    mount.appendChild(element('div', 'wc-title', `This workbook uses ${boundName}`));
    mount.appendChild(element('div', 'wc-text',
      `The add-in is on ${currentName}, so this workbook's Bifrost cells show an error instead of figures.`));

    const target = state.switchTarget;
    if (target) {
      this.button(mount, `Switch to ${named(target.name, boundName)}`, true, () => this.act(() => this.host.switchTo(target)));
    }
    const current = state.current;
    this.button(mount, `Use ${currentName} for this workbook`, !target, () => this.act(() => this.useCurrent(current)));
  }

  private button(mount: HTMLElement, label: string, primary: boolean, onClick: () => Promise<void>): void {
    const button = element('button', `ms-Button${primary ? ' ms-Button--primary' : ''} wc-btn`, label) as HTMLButtonElement;
    button.type = 'button';
    button.addEventListener('click', () => void onClick());
    mount.appendChild(button);
  }

  private mount(): HTMLElement {
    const existing = document.getElementById(this.mountId);
    if (existing) return existing;
    const mount = document.createElement('div');
    mount.id = this.mountId;
    const after = document.getElementById('company-switcher')
      || document.getElementById('sandbox-banner')
      || document.querySelector('header.ms-welcome__header');
    if (after?.parentNode) {
      after.parentNode.insertBefore(mount, after.nextSibling);
    } else {
      document.body.insertBefore(mount, document.body.firstChild);
    }
    return mount;
  }
}
